import type { ConversationMessage } from '@hero/schema';
import type { PendingConfirmation } from './types';

/**
 * Cards a persisted transcript still leaves open, mirroring the
 * server's own rule (`loadPendingConfirmationCards`): the newest
 * assistant message that carries `confirmation_card` widgets owns
 * them. Confirmed and declined cards are rewritten to plain text
 * server-side, so anything still shaped as a widget is genuinely
 * awaiting the user.
 *
 * The two sides have to agree on this: a card the server counts as
 * open but the panel never surfaces is exactly the state where the
 * agent offers to confirm something the user cannot see.
 *
 * `messages` arrives oldest-first, as
 * `GET /agent/conversations/messages` returns it.
 */
export const readOpenConfirmations = (
  messages: ConversationMessage[],
): PendingConfirmation[] => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== 'assistant') continue;
    const cards: PendingConfirmation[] = [];
    for (const block of message.content?.blocks ?? []) {
      if (
        block.kind === 'widget' &&
        block.widget.type === 'confirmation_card' &&
        block.widget.confirmationId
      ) {
        cards.push({
          id: block.widget.confirmationId,
          action: block.widget.action,
          payload: (block.widget.payload ?? {}) as Record<string, unknown>,
        });
      }
    }
    if (cards.length > 0) return cards;
  }
  return [];
};
